import { ReactElement, useState } from "react";
import {
  FormControl,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import StyledTableRow from "../../../components/styledTableRow";
import {
  IAcquisitionDataSchema,
  IDashboardAcquisitionEvent
} from "../types";
import { Add, Delete } from "@mui/icons-material";
import { getPalette, getPaletteColor } from "../../../configuration/configuration";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ConditionsDialog from "./conditionsDialog";
import { getUID } from "../../../system/utils";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TableContainer from "../../../components/tableContainer";
import ColorPicker from "../../../components/colorPicker";

interface IProps {
  events: IDashboardAcquisitionEvent[];
  updateEvents: (variables: IDashboardAcquisitionEvent[]) => void;
  schema: IAcquisitionDataSchema;
}

interface IDialogContext {
  open: boolean;
  selectedEvent: IDashboardAcquisitionEvent | null;
}

const maxVar = 4;

const EventsTable = ({ events, updateEvents, schema }: IProps): ReactElement => {
  const [ dialogContext, setDialogContext ] = useState<IDialogContext>({ open: false, selectedEvent: null });
  const [ colors, setColors ] = useState<string[]>( events.map((v: IDashboardAcquisitionEvent) => v.color));

  const addSeries = () => {
    const color = getPaletteColor();
    setColors([ ...colors, color]);

    updateEvents([
      ...events,
      {
        hash: getUID(),
        field: "",
        color: color,
        type: "VerticalLine",
        conditions: []
      } ]);
  };

  const removeSeries = (index: number) => {
    const newArray = [ ...events ];
    newArray.splice(index, 1);
    updateEvents(newArray);

    const newColors = [ ...colors];
    newColors.splice(index, 1);
    setColors(newColors);
  };

  const updateItemsSeries = (index: number, field: keyof IDashboardAcquisitionEvent, value: any) => {
    const newArray = [ ...events ];
    const item = newArray[index] as any;

    if (field === "color") {
      const newColors = [...colors];
      newColors[index] = value;
      setColors(newColors);
      item[field] = newColors[index];
    } else {

      item[field] = value;
    }

    updateEvents(newArray);
  };

  return (
    <TableContainer sx={{ maxHeight: 270 }}>
      <Table stickyHeader={true}  size={"small"}>
        <TableHead>
          <TableRow>
            <TableCell className="table-header" style={{ maxWidth: 24, width: 24, padding: 4 }}>
              <IconButton sx={{ padding: 2 }} style={{ maxWidth: 21 }} onClick={addSeries} disabled={events.length >= maxVar}>
                <Tooltip title={"Add variable"}>
                  <Add style={{ color: getPalette().statusColor.info.background }}/>
                </Tooltip>
              </IconButton>
            </TableCell>
            <TableCell className="table-header" style={{ minWidth: 180, maxWidth: 250 }}>
              Field - {events.length} / {maxVar}
            </TableCell>
            <TableCell className="table-header" style={{ minWidth: 100 }}>Type</TableCell>
            <TableCell className="table-header" style={{ minWidth: 50 }}>Color</TableCell>
            <TableCell className="table-header" style={{ minWidth: 50 }}>Conditions</TableCell>
            <TableCell className="table-header" style={{ minWidth: 50 }}/>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.map((row, index) => {
            return (
              <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`series-row-${index}`}>
                <TableCell  style={{ maxWidth: 24, width: 24, padding: 4, textAlign: "right" }}>
                  {index + 1}
                </TableCell>
                <TableCell>
                  <Select
                    variant="standard"
                    className="w-100"
                    value={row.field}
                    displayEmpty={true}
                    onChange={(event: SelectChangeEvent<string>) => updateItemsSeries(index, "field", event.target.value)}>
                    {Object.keys(schema.events).map(field => <MenuItem key={`field-${index}-${field}`} value={field}>{field}</MenuItem>)}
                  </Select>
                </TableCell>

                <TableCell>
                  <FormControl className="w-100">
                    <Select
                      variant="standard"
                      value={row.type}
                      displayEmpty={true}
                      onChange={(event: SelectChangeEvent<"VerticalLine" | "Area">) => updateItemsSeries(index, "type", event.target.value as ("VerticalLine" | "Area"))}>
                      <MenuItem value={"VerticalLine"}>Vertical Line</MenuItem>
                      <MenuItem value={"Area"}>Area</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>

                <TableCell>
                  <FormControl className="w-100">
                    {/*<input type="color" value={row.color} onChange={(event: ChangeEvent<HTMLInputElement>) => updateItemsSeries(index, "color", event.target.value as string)}/>*/}
                    <ColorPicker color={colors[index]} onChange={(color) => updateItemsSeries(index, "color", color)} disableAlpha={true}/>
                  </FormControl>
                </TableCell>

                <TableCell>
                  <IconButton sx={{ padding: 2 }}
                    disabled={row.field === ""}
                    onClick={() => setDialogContext({ open: true, selectedEvent: row })} >
                    <Tooltip title={"Conditions"}>
                      <MoreVertIcon style={{ color: getPalette().statusColor.info.background }}/>
                    </Tooltip>
                  </IconButton>
                </TableCell>

                <TableCell>
                  <IconButton sx={{ padding: 2 }} onClick={() => removeSeries(index)} >
                    <Tooltip title={"Remove event"}>
                      <Delete style={{ color: getPalette().statusColor.error.background }}/>
                    </Tooltip>
                  </IconButton>
                </TableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
      <ConditionsDialog
        schema={schema}
        event={dialogContext.selectedEvent}
        open={dialogContext.open}
        setOpen={() => setDialogContext({ open: false, selectedEvent: null })} />
    </TableContainer>
  );
};

export default EventsTable;
